import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Plus, CalendarDays } from 'lucide-react';
import { useLanguage } from '../../context/AppContext';

// ── Meal types (Déjeuner / Dîner / Souper / Collation) — "Repas" retiré pour éviter la redondance
const MEAL_TYPES = [
  { value: 'dejeuner', label: '🌅', titleFr: 'Déjeuner', titleEn: 'Breakfast' },
  { value: 'diner', label: '☀️', titleFr: 'Dîner', titleEn: 'Lunch' },
  { value: 'souper', label: '🌙', titleFr: 'Souper', titleEn: 'Dinner' },
  { value: 'collation', label: '🍎', titleFr: 'Collation', titleEn: 'Snack' },
];

// Helpers — local YYYY-MM-DD (avoid UTC drift on toISOString)
const formatLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const getTodayYMD = () => formatLocalYMD(new Date());
const getMinDateYMD = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return formatLocalYMD(d);
};

// ── 4 actions in a 2x3 grid — GLOW aesthetic (Iter. 411.ee)
// Each accent color is stored as `r,g,b` so we can drop it into rgba()
// shadows without recomputing hex→rgb on every render.
const ActionTile = ({ onClick, emoji, labelFr, labelEn, rgb, language, testId, colSpan = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex flex-col items-center justify-center gap-2 h-28 p-3 w-full min-w-0 rounded-xl transition-all active:scale-[0.98] hover:scale-[1.02] ${colSpan ? 'col-span-2' : ''}`}
    data-testid={testId}
    style={{
      background: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${rgb},0.22) 100%)`,
      border: `1px solid rgba(${rgb},0.5)`,
      boxShadow: `0 0 14px rgba(${rgb},0.4), inset 0 1px 0 rgba(255,255,255,0.05)`,
      backdropFilter: 'blur(6px)',
    }}
  >
    <span
      className="text-4xl leading-none"
      aria-hidden
      style={{ filter: `drop-shadow(0 0 8px rgba(${rgb},0.85))` }}
    >
      {emoji}
    </span>
    <span
      className="font-heading uppercase text-[11px] tracking-widest font-black truncate max-w-full"
      style={{ color: `rgba(${rgb},1)`, textShadow: `0 0 5px rgba(${rgb},0.55)` }}
    >
      {language === 'fr' ? labelFr : labelEn}
    </span>
  </button>
);

/**
 * AddMealOptionsModal — entry point for adding a meal (photo/gallery/favorites/label/describe).
 *
 * Phase 1 retroactive history (2026-04-29):
 *   - Optional `selectedDate` + `onDateChange` props let the caller wire a
 *     date picker on top of the action menu.
 *   - When date === today (default) → discreet "Aujourd'hui · changer" line
 *     (zero friction for the daily flow).
 *   - When date !== today → orange "MODE RATTRAPAGE · DD/MM" banner makes
 *     the historical context unmistakable so the user sees what they're
 *     about to log onto.
 *   - `dateFocus` prop lets the Mes-Repas entry highlight the date row to
 *     guide the user (since rattrapage is the whole point of that button).
 */
const AddMealOptionsModal = ({
  open,
  onOpenChange,
  photoMealType,
  onMealTypeChange,
  onPhotoClick,
  onGalleryClick,
  onDescribeClick,
  onLabelClick,
  onFavoritesClick,
  // Phase 1 retroactive — all optional; modal stays backward-compat
  selectedDate,
  onDateChange,
  dateFocus = false,
}) => {
  const { language, t } = useLanguage();
  const fr = language === 'fr';

  const todayYMD = useMemo(() => getTodayYMD(), []);
  const minDateYMD = useMemo(() => getMinDateYMD(), []);
  const effectiveDate = selectedDate || todayYMD;
  const isHistorical = effectiveDate !== todayYMD;
  const dateAvailable = typeof onDateChange === 'function';

  const formattedHistoricalLabel = useMemo(() => {
    if (!isHistorical) return '';
    try {
      // Local-noon to avoid TZ drift, then format
      const d = new Date(effectiveDate + 'T12:00:00');
      return d.toLocaleDateString(fr ? 'fr-FR' : 'en-US', {
        weekday: 'short', day: 'numeric', month: 'short',
      });
    } catch {
      return effectiveDate;
    }
  }, [effectiveDate, isHistorical, fr]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-sm overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 pr-8">
            <Plus className="w-5 h-5 text-primary shrink-0" />
            <span className="truncate">{t('modals.add_meal_title')}</span>
          </DialogTitle>
        </DialogHeader>

        {/* ─── Date picker — Phase 1 (2026-04-29) ───
            Discreet by default (today). Becomes a clear orange banner when
            user selects a past day. Hidden entirely if no `onDateChange`
            prop (backward-compat for any caller that doesn't wire it).
            Iter. 406.fr.13 — Mobile-safe layout: <input type="date">
            has a hardcoded min-width on Android/Samsung Chrome which used
            to overflow the modal and push the whole page sideways. Now the
            input is width-constrained and the label truncates gracefully. */}
        {dateAvailable && (
          <div
            className={`-mt-1 mb-1 px-2.5 py-1.5 rounded-lg border transition-all w-full overflow-hidden ${
              isHistorical
                ? 'bg-orange-500/10 border-orange-500/40'
                : dateFocus
                  ? 'bg-orange-500/5 border-orange-500/25'
                  : 'bg-secondary/40 border-border/50'
            }`}
            data-testid="meal-add-date-row"
          >
            <div className="flex items-center justify-between gap-2 w-full min-w-0">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <CalendarDays
                  className={`w-3.5 h-3.5 shrink-0 ${isHistorical ? 'text-orange-400' : 'text-muted-foreground'}`}
                />
                <span className={`text-[11px] uppercase tracking-widest font-bold truncate ${
                  isHistorical ? 'text-orange-300' : 'text-muted-foreground'
                }`}>
                  {isHistorical
                    ? (fr ? `Rattrapage · ${formattedHistoricalLabel}` : `Backfill · ${formattedHistoricalLabel}`)
                    : (fr ? "Aujourd'hui" : 'Today')}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 max-w-[55%]">
                <input
                  type="date"
                  value={effectiveDate}
                  min={minDateYMD}
                  max={todayYMD}
                  onChange={(e) => {
                    const v = e.target.value || todayYMD;
                    onDateChange(v === todayYMD ? null : v);
                  }}
                  className="h-6 w-[110px] text-[11px] bg-transparent border-0 p-0 m-0 outline-none cursor-pointer text-muted-foreground hover:text-foreground"
                  data-testid="meal-add-date-input"
                  aria-label={fr ? 'Choisir la date du repas' : 'Pick meal date'}
                />
                {isHistorical && (
                  <button
                    type="button"
                    onClick={() => onDateChange(null)}
                    className="text-[10px] uppercase tracking-widest font-bold text-orange-400 hover:text-orange-300 whitespace-nowrap"
                    data-testid="meal-add-reset-today"
                  >
                    {fr ? 'Auj.' : 'Today'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Meal Type — 4 options centered */}
        <div className="space-y-2 pb-2">
          <label className="text-sm font-medium text-foreground">
            {fr ? 'Type de repas' : 'Meal type'}
          </label>
          <div className="grid grid-cols-4 gap-1.5">
            {MEAL_TYPES.map(type => {
              const active = photoMealType === type.value;
              return (
                <button
                  key={type.value}
                  onClick={() => onMealTypeChange(type.value)}
                  className="flex flex-col items-center justify-center py-2 rounded-lg transition-all active:scale-95"
                  data-testid={`meal-type-${type.value}`}
                  style={{
                    background: active
                      ? 'linear-gradient(140deg, rgba(251,146,60,0.35) 0%, rgba(251,146,60,0.18) 100%)'
                      : 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(15,15,15,0.5) 45%, rgba(251,146,60,0.08) 100%)',
                    border: active
                      ? '1px solid rgba(251,146,60,0.75)'
                      : '1px solid rgba(251,146,60,0.2)',
                    boxShadow: active
                      ? '0 0 12px rgba(251,146,60,0.55), inset 0 1px 0 rgba(255,255,255,0.08)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <span
                    className="text-xl leading-none"
                    style={{ filter: active ? 'drop-shadow(0 0 5px rgba(251,146,60,0.9))' : 'none' }}
                  >
                    {type.label}
                  </span>
                  <span
                    className="text-[10px] mt-1 text-center leading-tight font-black uppercase tracking-wider"
                    style={{
                      color: active ? '#fed7aa' : 'rgba(148,163,184,0.75)',
                      textShadow: active ? '0 0 5px rgba(251,146,60,0.55)' : 'none',
                    }}
                  >
                    {fr ? type.titleFr : type.titleEn}
                  </span>
                </button>
              );
            })}
          </div>
          {photoMealType === 'collation' && (
            <p className="text-xs text-muted-foreground bg-yellow-500/10 p-2 rounded">
              {fr
                ? '💡 Les collations ont un pointage ajuste (50% des points) pour ne pas penaliser les petits repas.'
                : '💡 Snacks have adjusted scoring (50% of points) to not penalize small meals.'}
            </p>
          )}
        </div>

        {/* Actions — 2x3 GLOW grid (Iter. 411.ee) */}
        <div className="grid grid-cols-2 gap-3 py-4 w-full min-w-0">
          <ActionTile
            onClick={onPhotoClick}
            emoji="📸"
            labelFr="Photo"
            labelEn="Photo"
            rgb="251,146,60"
            language={language}
            testId="action-photo-btn"
          />
          <ActionTile
            onClick={onGalleryClick}
            emoji="🖼️"
            labelFr="Galerie"
            labelEn="Gallery"
            rgb="14,165,233"
            language={language}
            testId="action-gallery-btn"
          />
          <ActionTile
            onClick={onFavoritesClick}
            emoji="💖"
            labelFr="Favoris"
            labelEn="Favorites"
            rgb="236,72,153"
            language={language}
            testId="action-favorites-btn"
          />
          <ActionTile
            onClick={onLabelClick}
            emoji="🔍"
            labelFr="Étiquette"
            labelEn="Label"
            rgb="59,130,246"
            language={language}
            testId="action-label-btn"
          />
          <ActionTile
            onClick={onDescribeClick}
            emoji="✍️"
            labelFr="Décrire"
            labelEn="Describe"
            rgb="245,158,11"
            language={language}
            testId="action-describe-btn"
            colSpan
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddMealOptionsModal;
